import React, { forwardRef, useRef } from "react";
import { createPortal } from "react-dom";
import { useOverlayScrollIsolation } from "../../hooks/useOverlayScrollIsolation";
import type { IsolationMode } from "../../utils/overlayScrollIsolation";

type OverlayRootProps = React.HTMLAttributes<HTMLDivElement> & {
  enabled?: boolean;
  isolate?: IsolationMode;
  portal?: boolean;
};

const OverlayRoot = forwardRef<HTMLDivElement, OverlayRootProps>(function OverlayRoot(
  { enabled = true, isolate = "lock", portal = isolate === "lock", className, children, ...rest },
  forwardedRef
) {
  const localRef = useRef<HTMLDivElement | null>(null);

  const setRefs = (node: HTMLDivElement | null) => {
    localRef.current = node;
    if (typeof forwardedRef === "function") {
      forwardedRef(node);
    } else if (forwardedRef) {
      forwardedRef.current = node;
    }
  };

  useOverlayScrollIsolation(localRef, enabled, isolate);

  const node = (
    <div
      ref={setRefs}
      data-overlay-root=""
      className={className}
      {...rest}
    >
      {children}
    </div>
  );

  if (portal && typeof document !== "undefined") {
    return createPortal(node, document.body);
  }

  return node;
});

export default OverlayRoot;
